/**
 * Globe Ping Domain
 * Manages team globe ping communication state and operations. Only your team
 * sees a ping — the server sends it to the team — so a ping carries no colour:
 * the globe draws every one in the accent.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';
import { PING_CONFIG } from './globe-ping-config';
import { sendToMatch } from '../services/nakama/match-send';

// ==================== TYPES ====================

/**
 * Active ping displayed for a player
 */
interface ActivePing {
  userId: string;
  lat: number;
  lng: number;
  timestamp: number;
  expiresAt: number;
}

/**
 * Ping settings
 */
interface PingSettings {
  displayDuration: number; // seconds
}

// ==================== STORE ====================

interface GlobePingState {
  activePings: Map<string, ActivePing>; // Map of userId -> ActivePing
  settings: PingSettings;
  _version: number; // Internal version counter to force re-renders
  addActivePing: (userId: string, lat: number, lng: number) => void;
  removePing: (userId: string) => void;
  reset: () => void;
}

const initialState = {
  activePings: new Map<string, ActivePing>(),
  settings: {
    displayDuration: PING_CONFIG.displayDurationSeconds,
  },
  _version: 0,
};

/**
 * Globe Ping Store
 * Manages active ping state for all players
 */
export const useGlobePingStore = create<GlobePingState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Add an active ping for a user (replaces any existing ping)
       */
      addActivePing: (userId: string, lat: number, lng: number) => {
        const now = Date.now();
        const duration = get().settings.displayDuration * 1000; // convert to ms
        const expiresAt = now + duration;

        const ping: ActivePing = {
          userId,
          lat,
          lng,
          timestamp: now,
          expiresAt,
        };

        console.log('[GlobePingStore] Adding ping:', { userId, lat, lng, expiresAt });

        set((state) => {
          const newMap = new Map(state.activePings);
          newMap.set(userId, ping);
          return { 
            activePings: newMap,
            _version: state._version + 1, // Increment version to force re-render
          };
        }, false, 'addActivePing');

        // Schedule removal after duration
        setTimeout(() => {
          const currentPing = get().activePings.get(userId);
          if (currentPing && currentPing.timestamp === ping.timestamp) {
            get().removePing(userId);
          }
        }, duration);
      },

      /**
       * Remove a ping for a user
       */
      removePing: (userId: string) => {
        console.log('[GlobePingStore] Removing ping for user:', userId);
        set((state) => {
          const newMap = new Map(state.activePings);
          newMap.delete(userId);
          return { 
            activePings: newMap,
            _version: state._version + 1, // Increment version to force re-render
          };
        }, false, 'removePing');
      },

      /**
       * Reset the ping store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'globe-ping-store',
    }
  )
);

// ==================== HANDLERS ====================

/**
 * Handle OpCodePingReceived message
 * Displays ping for the sending user
 */
export function handlePingReceived(data: { user_id: string; lat: number; lng: number }) {
  console.log('[PingHandler] Ping received:', data);
  
  const { user_id, lat, lng } = data;
  
  // Validate coordinates
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    console.error('[PingHandler] Invalid coordinates:', { lat, lng });
    return;
  }

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    console.error('[PingHandler] Coordinates out of range:', { lat, lng });
    return;
  }

  useGlobePingStore.getState().addActivePing(user_id, lat, lng);
}

// ==================== SERVICE METHODS ====================

/**
 * Send a globe ping to team members
 * @param lat - Latitude coordinate (-90 to 90)
 * @param lng - Longitude coordinate (-180 to 180)
 */
export async function sendPing(lat: number, lng: number): Promise<void> {
  // Validate coordinates
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    console.error('[PingService] Invalid coordinates:', { lat, lng });
    return;
  }

  if (lat < -90 || lat > 90) {
    console.error('[PingService] Invalid latitude (must be -90 to 90):', lat);
    return;
  }

  if (lng < -180 || lng > 180) {
    console.error('[PingService] Invalid longitude (must be -180 to 180):', lng);
    return;
  }

  const message = {
    type: MessageType.MessageTypeSendPing,
    lat,
    lng,
  };

  console.log('[PingService] Sending ping:', { lat, lng }, 'Message:', message);

  await sendToMatch(OpCode.OpCodeSendPing, message, 'a globe ping');
}
